import { colors, colorsDark, colorsLight } from './colors';

const sharedTypography = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  textTransform: 'none',
  h1: {
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: 1.3,
    margin: '24px 0',
  },
  h2: {
    fontSize: '22px',
    fontWeight: 600,
    lineHeight: 1.35,
    margin: '20px 0',
  },
  h3: {
    fontWeight: 600,
    fontSize: '17px',
    lineHeight: 1.4,
    margin: '16px 0',
  },
  h4: {
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: 1.4,
    margin: '12px 0',
  },
  h5: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: 1.5,
    margin: '8px 0',
  },
  h6: {
    fontSize: '11px',
    fontWeight: 500,
    lineHeight: 1.5,
    margin: '6px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  body1: {
    fontSize: '14px',
    lineHeight: 1.5,
  },
  body2: {
    fontSize: '12px',
    lineHeight: 1.5,
  },
};

const themeSet = {
  light: {
    palette: {
      type: 'light',
      primary: {
        main: colorsLight.primary.main,
      },
      secondary: {
        main: colors.brown[400],
      },
      error: {
        main: colors.red[500],
      },
      warning: {
        main: colors.orange[500],
      },
      info: {
        main: colors.yellow[500],
      },
      success: {
        main: colors.green[500],
      },
      background: {
        main: colors.white,
        gray: colors.grey[100],
        paper: 'rgba(248, 250, 252, 0.92)',
      },
      // standardowe kolory
      black: colors.black,
      blue: colors.blue[500],
      green: colors.green[1],
      red: colors.red[500],
      white: colors.white,
      darkGrey: colors.grey[600],
      lightGrey: colors.grey[350],
      blackAlfa: colors.blackRgba[500],
      blackAlfaHover: colors.blackRgba[600],
      darkBlackAlfa: colors.blackRgba[800],
      darkBlackAlfaHover: colors.blackRgba[900],
      invertIcon: 'invert(0)',
      invertBlackIcon: 'invert(1)',
      // kolory dla elementów formularza nie nadpisanych w overwrite
      selectMenu: {
        textColor: `${colors.grey[600]} !important`,
        backgroundColor: colors.white,
      },
      labelColor: colors.grey[600],
      // generalne style używane w wielu/kilku komponentach
      fontColor: colors.white,
      backgroundColor: colors.grey[10],
      iconDivider: colors.grey[100],
      iconColor: colors.grey[600],
      sideBarBackgroundColor: colors.grey[900],
      defaultLanguageColor: colors.blackRgba[540],
      tableRow: colors.colorsRgba[500],
      dashboard: {
        filters: {
          areaShowBorder: '10px',
          areaShowTransition: 'ease 0.2s',
          buttonBorder: '5px',
          buttonBoxShadow: `0px 0px 10px 0px ${colors.white}`,
          buttonBoxShadowDark: `0px 0px 10px 0px ${colors.black}`,
          buttonBackgroundColor: '#06B6D4',
          buttonColor: '#FFFFFF',
          textShadow: `0px 0px 10px ${colors.black}`,
        },
      },
      popupBorderColor: colors.grey[200],
      // kolory używane w pojedycznych komponentach
      activationModalGreyBackgroundColor: colors.blackRgba[400],
      deleteConfirmationModalTextareaColor: colors.grey[300],
      detailsCardDescColor: colors.grey[400],
      displayUsersActionTitleColor: colors.grey[350],
      existingOrganizations: {
        cardColor: colors.white,
        hoverCardColor: colors.grey[10],
        linkColor: colors.grey[40],
      },
      uavoDetailsBorderRightColor: colors.grey[70],
      logoBackground: colors.grey[999],
    },
    typography: {
      color: {
        primary: '#0F172A',
        secondary: '#334155',
      },
      ...sharedTypography,
    },
    overrides: {
      MuiCssBaseline: {
        '@global': {
          '::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '::-webkit-scrollbar-thumb': {
            background: 'rgba(148, 163, 184, 0.4)',
            borderRadius: '3px',
            '&:hover': {
              background: 'rgba(148, 163, 184, 0.6)',
            },
          },
          '::-webkit-scrollbar-track': {
            background: 'transparent',
          },
        },
      },
      MuiMenuItem: {
        root: {
          color: colors.grey[600],
          padding: '10px 45px 10px 15px',
          '&:hover': {
            backgroundColor: `${colors.grey[600]} !important`,
            color: `${colors.white} !important`,
          },
        },
      },
      MuiInputBase: {
        input: {
          color: colors.grey[600],
          '&:-webkit-autofill': {
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: colors.grey[600],
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          borderRadius: '6px',
          transition: 'border-color 0.2s ease',
          '&:hover $notchedOutline': {
            borderColor: 'rgba(14, 165, 233, 0.4)',
          },
          '&.Mui-focused $notchedOutline': {
            border: '1px solid #06B6D4',
            borderColor: '#06B6D4',
          },
        },
        notchedOutline: {
          borderColor: colors.grey[100],
        },
        inputMultiline: {
          resize: 'none',
        },
      },
      MuiTextField: {
        root: {
          color: colors.grey[600],
          fontSize: '14px',
          lineHeight: 'normal',
          transition: 'color 0.2s ease',
          '&:disabled': {
            backgroundColor: colors.grey[100],
            color: colors.grey[600],
            cursor: 'not-allowed',
          },
        },
      },
      MuiButton: {
        root: {
          backgroundColor: colorsLight.primary.main,
          borderRadius: '6px',
          borderWidth: '1px',
          boxShadow: 'none !important',
          color: colors.white,
          cursor: 'pointer',
          fontSize: '0.8125rem',
          fontWeight: 600,
          letterSpacing: '0.02em',
          lineHeight: '1.35em',
          margin: '10px 1px',
          opacity: 1,
          padding: '8px 16px',
          textTransform: 'none',
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            backgroundColor: '#0284C7',
            boxShadow: '0 2px 8px rgba(14, 165, 233, 0.25) !important',
            color: colors.white,
            opacity: 1,
          },
          '&.Mui-disabled': {
            backgroundColor: '#CBD5E1',
            color: '#64748B',
            cursor: 'not-allowed',
            opacity: 1,
            pointerEvents: 'visible',
          },
          '@media (hover: none)': {
            '&:hover': {
              backgroundColor: `${colorsLight.primary.main} !important`,
              opacity: 1,
            },
          },
        },
        text: {
          padding: '8px 16px',
        },
        contained: {
          padding: '10px 20px',
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
          border: '1px solid',
          borderColor: 'transparent',
          opacity: 1,
          '&:hover': {
            color: colors.white,
            backgroundColor: '#0284C7',
          },
        },
        outlined: {
          padding: '8px 16px',
          opacity: 1,
          backgroundColor: 'transparent',
          border: '1px solid #0EA5E9',
          color: '#0EA5E9',
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            color: '#0EA5E9',
            backgroundColor: 'rgba(14, 165, 233, 0.08)',
            borderColor: '#38BDF8',
          },
        },
      },
    },
  },
  dark: {
    palette: {
      type: 'dark',
      primary: {
        main: colorsDark.primary.main,
      },
      secondary: {
        main: colorsDark.secondary.main,
      },
      error: {
        main: colors.red[500],
      },
      warning: {
        main: colors.orange[500],
      },
      info: {
        main: colors.yellow[500],
      },
      success: {
        main: colors.green[500],
      },
      background: {
        default: colors.blueGrey[800],
        paper: 'rgba(15, 23, 41, 0.95)',
      },
      text: {
        primary: `${colors.whiteRgba[800]} !important`,
        secondary: '#94A3B8',
      },
      // standardowe kolory
      black: colors.black,
      blue: colors.blue[400],
      green: colors.green[1],
      red: colors.red[500],
      white: colors.white,
      darkGrey: colors.grey[600],
      lightGrey: colors.grey[350],
      blackAlfa: colors.blackRgba[500],
      blackAlfaHover: colors.blackRgba[600],
      invertIcon: 'invert(1)',
      invertBlackIcon: 'invert(0)',
      // kolory dla elementów formularza nie nadpisanych w overwrite
      selectMenu: {
        textColor: colors.white,
        backgroundColor: colors.blueGrey[700],
      },
      labelColor: colors.white,
      // generalne style używane w wielu/kilku komponentach
      fontColor: colors.white,
      backgroundColor: `linear-gradient(${colors.blueGrey[800]},${colors.blueGrey[900]})`,
      iconDivider: colors.grey[100],
      iconColor: colors.grey[600],
      sideBarBackgroundColor: colors.grey[900],
      defaultLanguageColor: colors.whiteRgba[800],
      tableRow: colors.colorsRgba[600],
      dashboard: {
        filters: {
          areaShowBorder: '10px',
          areaShowTransition: 'ease 0.2s',
          buttonBorder: '5px',
          buttonBoxShadow: `0px 0px 10px 0px ${colors.white}`,
          buttonBoxShadowDark: `0px 0px 10px 0px ${colors.black}`,
          buttonBackgroundColor: '#06B6D4',
          buttonColor: '#FFFFFF',
          textShadow: `0px 0px 10px ${colors.black}`,
        },
      },
      popupBorderColor: colors.grey[200],
      // kolory używane w pojedycznych komponentach
      activationModalGreyBackgroundColor: colors.blackRgba[400],
      deleteConfirmationModalTextareaColor: colors.grey[300],
      detailsCardDescColor: colors.grey[400],
      displayUsersActionTitleColor: colors.grey[350],
      existingOrganizations: {
        cardColor: colors.blueGrey[700],
        overCardColor: colors.blueGrey[800],
        linkColor: '#38BDF8',
      },
      uavoDetailsBorderRightColor: '#0EA5E9',
      // kolory tylko dla dark
      darkDisplayUser: {
        backgroundColor: colors.blue[600],
        darkerBlueForGradient: colors.blue[700],
        decorationPrimaryColor: colors.transparent,
        decorationSecondColor: colors.colorsRgba[600],
      },
      logoBackground: colors.grey[999],
    },
    typography: {
      color: {
        primary: '#F8FAFC',
        secondary: '#F8FAFC',
      },
      ...sharedTypography,
    },
    overrides: {
      MuiCssBaseline: {
        '@global': {
          '::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '::-webkit-scrollbar-thumb': {
            background: 'rgba(148, 163, 184, 0.3)',
            borderRadius: '3px',
            '&:hover': {
              background: 'rgba(148, 163, 184, 0.5)',
            },
          },
          '::-webkit-scrollbar-track': {
            background: 'transparent',
          },
        },
      },
      MuiSelect: {
        icon: {
          color: `${colors.white} !important`,
        },
      },
      MuiMenuItem: {
        root: {
          color: colors.white,
          padding: '10px 45px 10px 15px',
          '&:hover': {
            backgroundColor: `${colors.blackRgba[340]} !important`,
          },
          '&$selected': {
            backgroundColor: colors.blackRgba[200],
          },
        },
      },
      MuiInputBase: {
        input: {
          color: colors.white,
          '&:-webkit-autofill': {
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: colors.white,
          },
          '&$disabled': {
            backgroundColor: colors.grey[900],
            color: '#94A3B8',
            cursor: 'not-allowed',
          },
        },
      },
      MuiTextField: {
        root: {
          backgroundColor: colors.transparent,
          borderRadius: '6px',
          color: colors.white,
          fontSize: '14px',
          lineHeight: 'normal',
          padding: '0px',
          transition: 'color 0.2s ease, background-color 0.2s ease',
          '&:focus': {
            border: '1px solid #06B6D4',
            boxShadow: 'none',
            color: colors.white,
            outline: '0 !important',
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          borderRadius: '6px',
          '&:hover $notchedOutline': {
            borderColor: 'rgba(6, 182, 212, 0.4)',
          },
          '&.Mui-focused $notchedOutline': {
            border: '1px solid #06B6D4',
            borderColor: '#06B6D4',
          },
        },
        notchedOutline: {
          borderColor: 'rgba(148, 163, 184, 0.3)',
        },
      },
      MuiButton: {
        root: {
          background: '#0EA5E9',
          backgroundImage: 'none',
          backgroundPosition: 'unset',
          backgroundSize: 'unset',
          border: 'none',
          borderRadius: '6px',
          boxShadow: 'none !important',
          color: colors.white,
          cursor: 'pointer',
          fontSize: '0.8125rem',
          fontWeight: 600,
          letterSpacing: '0.02em',
          lineHeight: '1.35em',
          margin: '10px 1px',
          padding: '8px 16px',
          textAlign: 'center',
          textTransform: 'none',
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
          userSelect: 'none',
          '&:hover': {
            backgroundColor: '#0284C7',
            boxShadow: '0 2px 8px rgba(14, 165, 233, 0.3) !important',
          },
          '&.Mui-disabled': {
            backgroundColor: '#1E293B',
            color: '#475569',
            cursor: 'not-allowed',
          },
        },
        text: {
          padding: '8px 16px',
        },
        contained: {
          padding: '10px 20px',
          transition: 'background-color 0.2s ease',
          border: '1px solid',
          borderColor: 'transparent',
          opacity: 1,
          '&:hover': {
            color: colors.white,
            backgroundColor: '#0284C7',
          },
        },
        outlined: {
          padding: '8px 16px',
          opacity: 1,
          backgroundColor: 'transparent',
          border: '1px solid #0EA5E9',
          color: '#0EA5E9',
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            color: '#38BDF8',
            backgroundColor: 'rgba(14, 165, 233, 0.08)',
            borderColor: '#38BDF8',
          },
        },
      },
    },
  },
};

export default themeSet;
